import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AcceptInviteInput,
  CollectionMeta,
  InviteMemberInput,
  ListMembersQuery,
  SessionResult,
  TeamMember,
  UpdateMemberInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['users'] as const;

export interface MemberPage {
  items: TeamMember[];
  meta: CollectionMeta;
}

export async function listMembers(query: ListMembersQuery = {}): Promise<MemberPage> {
  const res = await apiClient.getCollection<TeamMember>('/users', {
    query: { page: query.page, pageSize: query.pageSize },
  });
  return { items: res.data, meta: res.meta };
}
export const inviteMember = (input: InviteMemberInput) =>
  apiClient.post<TeamMember>('/users', input);
export const resendInvite = (tenantUserId: string) =>
  apiClient.post<void>(`/users/${tenantUserId}/resend-invite`);
export const updateMember = (tenantUserId: string, patch: UpdateMemberInput) =>
  apiClient.patch<TeamMember>(`/users/${tenantUserId}`, patch);
export const removeMember = (tenantUserId: string) =>
  apiClient.delete<void>(`/users/${tenantUserId}`);
/** Public - the accept-invite link may be opened with no existing session. */
export const acceptInvite = (input: AcceptInviteInput) =>
  apiClient.post<SessionResult>('/users/accept-invite', input);

/** Active teammates for an "Assigned to" picker (name + id only). */
export interface AssignableMember {
  tenantUserId: string;
  displayName: string;
  title: string | null;
}

export const listAssignableMembers = () => apiClient.get<AssignableMember[]>('/users/assignable');

export function useAssignableMembers(enabled = true) {
  return useQuery<AssignableMember[], ApiClientError>({
    queryKey: [...KEY, 'assignable'],
    queryFn: listAssignableMembers,
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useMembers(query: ListMembersQuery = {}) {
  return useQuery<MemberPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listMembers(query),
  });
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation<TeamMember, ApiClientError, InviteMemberInput>({
    mutationFn: inviteMember,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useResendInvite() {
  return useMutation<void, ApiClientError, string>({ mutationFn: resendInvite });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation<
    TeamMember,
    ApiClientError,
    { tenantUserId: string; patch: UpdateMemberInput }
  >({
    mutationFn: ({ tenantUserId, patch }) => updateMember(tenantUserId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: removeMember,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAcceptInvite() {
  return useMutation<SessionResult, ApiClientError, AcceptInviteInput>({
    mutationFn: acceptInvite,
  });
}
